import { API_PATH } from '../types/shared';
import queryInfoUnjoined from './query-info/unjoined.json';
import queryInfoPreJoined from './query-info/pre-joined.json';
import queryInfoOnProgress from './query-info/on-progress.json';
import queryInfoTaskJumpTest from './query-info/task-jump-test.json';
import queryInfoEnded from './query-info/ended.json';
import queryInfoErrorOffline from './query-info/error-offline.json';
import queryRewardsNoRewards from './query-rewards/no-rewards.json';
import queryRewardsFreeCardUnused from './query-rewards/free-card-unused.json';
import queryRewardsFreeCardUsed from './query-rewards/free-card-used.json';
import queryRewardsCouponOnly from './query-rewards/coupon-only.json';
import queryRewardsCouponRewards from './query-rewards/coupon-rewards.json';
import queryRewardsFreeCardCrossCycle from './query-rewards/free-card-cross-cycle.json';
import queryPopupCacheNotShown from './query-popup-cache/not-shown.json';
import queryPopupCacheAllShown from './query-popup-cache/all-shown.json';
import queryPopupCacheFreeOrderNotShown from './query-popup-cache/free-order-not-shown.json';
import activityCacheHit from './activity-cache/pre-selected-city-hit.json';
import activityCacheMiss from './activity-cache/pre-selected-city-miss.json';
import activityCacheSubscribedHit from './activity-cache/subscribed-hit.json';
import cardGroupDetailShanghai from './card-group-detail/shanghai.json';
import queryAppointmentNotSubscribed from './query-appointment/not-subscribed.json';
import leaderboardWithData from './freeorder-leaderboard/with-data.json';
import leaderboardNoData from './freeorder-leaderboard/no-data.json';
import assistRecordWithRecords from './assist-record/with-records.json';
import assistRecordNoRecords from './assist-record/no-records.json';
/**
 * 前端自测场景枚举。
 * 通过环境变量 MOCK_SCENE 指定场景名，useRequest 全局拦截对应 path 的请求。
 *
 * 使用方式（本地 dev server）：
 *   MOCK_SCENE=unjoined npm run start
 *   MOCK_SCENE=on-progress npm run start
 *   MOCK_SCENE=free-card-unused npm run start
 *   ...
 *
 * 场景与节点对照见 src/ai-docs/mock-map.yaml
 */
export var MockScene;
(function (MockScene) {
    /** 未参与态：弹出城市选择弹窗 */
    MockScene["Unjoined"] = "unjoined";
    /** R001 预参与态：query-info 返回未参与，但 activity-cache 命中预选城市（北京），init 升级为 PRE_JOINED */
    MockScene["PreJoined"] = "pre-joined";
    /** 参与中态：有助力记录 → hasAssistRecord=true → 不展示角标 */
    MockScene["OnProgressWithAssist"] = "on-progress-with-assist";
    /** 参与中态：无助力记录 → hasAssistRecord=false → 展示 onProgressBadgeText 角标 */
    MockScene["OnProgressNoAssist"] = "on-progress-no-assist";
    /** 免单卡未使用态 */
    MockScene["FreeCardUnused"] = "free-card-unused";
    /** 免单卡已使用态 */
    MockScene["FreeCardUsed"] = "free-card-used";
    /** 仅有优惠券奖励 */
    MockScene["CouponOnly"] = "coupon-only";
    /** 优惠奖励：参与中态 + 多条业务线优惠券（境内酒店/民宿/景点，多态），用于自测奖励进度弹窗优惠奖励 Tab */
    MockScene["CouponRewards"] = "coupon-rewards";
    /** 跨周期免单卡（isJoined=false 但有免单卡） */
    MockScene["FreeCardCrossCycle"] = "free-card-cross-cycle";
    /** 活动已结束态（未预约） */
    MockScene["EndedNotSubscribed"] = "ended-not-subscribed";
    /** 活动已结束态（已预约） */
    MockScene["EndedSubscribed"] = "ended-subscribed";
    /** 活动下线/异常态 */
    MockScene["ErrorOffline"] = "error-offline";
    /** 任务跳转自测：参与中态 + 6 业务线 × 浏览/下单任务（验证拼链跳转） */
    MockScene["TaskJumpTest"] = "task-jump-test";
})(MockScene || (MockScene = {}));
/** 各 mock 场景中文 label（开发期场景切换面板展示用） */
export const MOCK_SCENE_LABELS = {
    [MockScene.Unjoined]: '未参与',
    [MockScene.PreJoined]: '预参与',
    [MockScene.OnProgressWithAssist]: '参与中·有助力',
    [MockScene.OnProgressNoAssist]: '参与中·无助力',
    [MockScene.FreeCardUnused]: '免单卡·未使用',
    [MockScene.FreeCardUsed]: '免单卡·已使用',
    [MockScene.CouponOnly]: '仅优惠券',
    [MockScene.CouponRewards]: '优惠奖励',
    [MockScene.FreeCardCrossCycle]: '跨周期免单卡',
    [MockScene.EndedNotSubscribed]: '已结束·未预约',
    [MockScene.EndedSubscribed]: '已结束·已预约',
    [MockScene.ErrorOffline]: '下线/异常',
    [MockScene.TaskJumpTest]: '任务跳转自测'
};
/** 全部可切换场景（显式列出，避免 Object.values 的低端浏览器 polyfill 依赖） */
export const ALL_MOCK_SCENES = [MockScene.Unjoined, MockScene.PreJoined, MockScene.OnProgressWithAssist, MockScene.OnProgressNoAssist, MockScene.FreeCardUnused, MockScene.FreeCardUsed, MockScene.CouponOnly, MockScene.CouponRewards, MockScene.FreeCardCrossCycle, MockScene.EndedNotSubscribed, MockScene.EndedSubscribed, MockScene.ErrorOffline, MockScene.TaskJumpTest];
// ─── 开发自测默认 mock 场景 ───────────────────────────────────────────────────
// 自测时改为对应枚举值，提交前必须改回 undefined。运行期由场景切换面板通过 setActiveMockScene 切换（纯变量，不走链接参数）。
export const DEV_MOCK_SCENE = undefined;
// 当前生效场景（运行期可变的模块级变量）+ 订阅者集合：切换时通知订阅方（如数据 hook 重新拉取、面板重渲染）
let _activeMockScene = DEV_MOCK_SCENE;
const sceneListeners = new Set();
/** 当前生效 mock 场景（无值 = 非 mock 模式）。useRequest / 切换面板均以此为准 */
export function resolveActiveScene() {
    return _activeMockScene;
}
/** 切换当前 mock 场景（纯变量切换，不刷新页面、不改 URL）；变更后通知所有订阅者重新拉取 / 重渲染 */
export function setActiveMockScene(scene) {
    if (scene === _activeMockScene)
        return;
    _activeMockScene = scene;
    sceneListeners.forEach(fn => {
        try {
            fn();
        }
        catch {
            // 单个订阅者异常不影响其它订阅者
        }
    });
}
/** 订阅场景变更（返回取消订阅函数）。数据 hook 用它在切换后重新拉取，面板用它高亮当前场景 */
export function subscribeMockScene(listener) {
    sceneListeners.add(listener);
    return () => {
        sceneListeners.delete(listener);
    };
}
/** 各场景对应的接口 mock 数据 */
const MOCK_SCENE_MAP = {
    [MockScene.Unjoined]: {
        [API_PATH.QUERY_INFO]: queryInfoUnjoined,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheNotShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardNoData
    },
    [MockScene.PreJoined]: {
        // query-info 返回预参与态（selectedCardGroups 为空，所有城市 cards 为 null）
        // activity-cache 命中预选城市（上海）→ init edge 3 升级为 PRE_JOINED
        // card-group-detail 返回上海地标列表，供 useCardGroupDetail hook 使用
        [API_PATH.QUERY_INFO]: queryInfoPreJoined,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheNotShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheHit,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.CARD_GROUP_DETAIL]: cardGroupDetailShanghai,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.OnProgressWithAssist]: {
        // 有助力记录 → hasAssistRecord=true → 不展示角标
        [API_PATH.QUERY_INFO]: queryInfoOnProgress,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData,
        [API_PATH.ASSIST_RECORD]: assistRecordWithRecords
    },
    [MockScene.OnProgressNoAssist]: {
        // 无助力记录 → hasAssistRecord=false → 展示 onProgressBadgeText 角标
        [API_PATH.QUERY_INFO]: queryInfoOnProgress,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData,
        [API_PATH.ASSIST_RECORD]: assistRecordNoRecords
    },
    [MockScene.FreeCardUnused]: {
        [API_PATH.QUERY_INFO]: queryInfoOnProgress,
        [API_PATH.REWARDS]: queryRewardsFreeCardUnused,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheFreeOrderNotShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.FreeCardUsed]: {
        [API_PATH.QUERY_INFO]: queryInfoOnProgress,
        [API_PATH.REWARDS]: queryRewardsFreeCardUsed,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.CouponOnly]: {
        [API_PATH.QUERY_INFO]: queryInfoOnProgress,
        [API_PATH.REWARDS]: queryRewardsCouponOnly,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheNotShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.CouponRewards]: {
        // 参与中态 + 多业务线优惠券；弹窗标记已展示避免奖励弹窗干扰，点「看奖励」打开奖励进度弹窗看优惠奖励 Tab
        [API_PATH.QUERY_INFO]: queryInfoOnProgress,
        [API_PATH.REWARDS]: queryRewardsCouponRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.FreeCardCrossCycle]: {
        [API_PATH.QUERY_INFO]: queryInfoUnjoined,
        [API_PATH.REWARDS]: queryRewardsFreeCardCrossCycle,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheFreeOrderNotShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.EndedNotSubscribed]: {
        [API_PATH.QUERY_INFO]: queryInfoEnded,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData
    },
    [MockScene.EndedSubscribed]: {
        // ACTIVITY_CACHE 返回缓存命中（IS_SUBSCRIBED_CACHE_KEY='1'），isSubscribed=true
        [API_PATH.QUERY_INFO]: queryInfoEnded,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheSubscribedHit,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardNoData
    },
    [MockScene.ErrorOffline]: {
        [API_PATH.QUERY_INFO]: queryInfoErrorOffline,
        [API_PATH.REWARDS]: queryRewardsNoRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardNoData
    },
    [MockScene.TaskJumpTest]: {
        // 参与中态 + 多业务线任务；其余接口复用参与中态 mock，弹窗全部标记已展示避免奖励弹窗干扰
        // REWARDS 用优惠奖励 mock（多张券）+ ASSIST_RECORD 多条记录，便于一并自测任务/券/记录三处列表滚动
        [API_PATH.QUERY_INFO]: queryInfoTaskJumpTest,
        [API_PATH.REWARDS]: queryRewardsCouponRewards,
        [API_PATH.QUERY_POPUP_CACHE]: queryPopupCacheAllShown,
        [API_PATH.ACTIVITY_CACHE]: activityCacheMiss,
        [API_PATH.QUERY_APPOINTMENT]: queryAppointmentNotSubscribed,
        [API_PATH.FREE_ORDER_CARD_LEADERBOARD]: leaderboardWithData,
        [API_PATH.ASSIST_RECORD]: assistRecordWithRecords
    }
};
/**
 * 根据指定场景和请求 path 返回对应的 mock 数据。
 * scene 为 undefined 时返回 undefined，走真实请求。
 */
export function getMockDataByPath(path, scene) {
    if (!scene)
        return undefined;
    return MOCK_SCENE_MAP[scene]?.[path];
}
